<script lang="ts" setup>
import {
  ArrowLeftOutlined,
  LockOutlined,
  MailOutlined,
  SafetyOutlined,
} from '@ant-design/icons-vue'
import { toTypedSchema } from '@vee-validate/zod'
import { message } from 'ant-design-vue'
import { useField, useForm } from 'vee-validate'
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { z } from 'zod'
import { neon } from '@/services/neon'

const router = useRouter()
const step = ref<'email' | 'otp'>('email')
const authError = ref<string | null>(null)
const email = ref('')
const loading = ref(false)

const emailSchema = z.object({
  email: z.string().nonempty('Email is required').email('Please input a valid email!'),
})

const resetSchema = z
  .object({
    otp: z.string().nonempty('Verification code is required').length(6, 'Code must be 6 digits'),
    password: z
      .string()
      .nonempty('Password is required')
      .min(6, 'Password must be at least 6 characters'),
    confirmPassword: z.string().nonempty('Please confirm your password'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })

const { handleSubmit: handleEmailSubmit, errors: emailErrors } = useForm<{ email: string }>({
  validationSchema: toTypedSchema(emailSchema as any),
  initialValues: { email: '' },
})

const { value: emailField } = useField<string>('email')
const emailFieldValue = computed(() => emailField.value)

const { handleSubmit: handleResetSubmit, errors: resetErrors } = useForm<{
  otp: string
  password: string
  confirmPassword: string
}>({
  validationSchema: toTypedSchema(resetSchema as any),
  initialValues: { otp: '', password: '', confirmPassword: '' },
})

const { value: otp } = useField<string>('otp')
const { value: newPassword } = useField<string>('password')
const { value: confirmPassword } = useField<string>('confirmPassword')

const sendOtp = handleEmailSubmit(async (values) => {
  try {
    loading.value = true
    authError.value = null
    const res = await neon.auth.forgetPassword.emailOtp({ email: values.email })
    if (res.error) {
      throw new Error(res.error.message ?? 'Failed to send reset code')
    }
    email.value = values.email
    message.success('Reset code sent! Check your email.')
    step.value = 'otp'
  } catch (err) {
    if (err instanceof Error && err.message) {
      authError.value = err.message
    } else {
      authError.value = 'Failed to send reset code.'
    }
  } finally {
    loading.value = false
  }
})

const resetPassword = handleResetSubmit(async (values) => {
  try {
    loading.value = true
    authError.value = null
    const res = await neon.auth.resetPassword({
      newPassword: values.password,
      token: values.otp,
    })
    if (res.error) {
      throw new Error(res.error.message ?? 'Failed to reset password')
    }
    message.success('Password reset successfully! Please log in.')
    router.push('/login')
  } catch (err) {
    if (err instanceof Error && err.message) {
      authError.value = err.message
    } else {
      authError.value = 'Failed to reset password.'
    }
  } finally {
    loading.value = false
  }
})
</script>
<template>
  <div class="forgot-container">
    <a-card class="forgot-form" title="Reset Password">
      <template v-if="step === 'email'">
        <p style="text-align: center; margin-bottom: 24px">
          Enter your email address and we'll send you a verification code to reset your password.
        </p>

        <a-form @submit.prevent="sendOtp">
          <a-form-item
            label="Email"
            :validate-status="emailErrors.email ? 'error' : ''"
            :help="emailErrors.email"
          >
            <a-input v-model:value="emailField" autocomplete="email">
              <template #prefix>
                <MailOutlined class="site-form-item-icon" />
              </template>
            </a-input>
          </a-form-item>

          <a-form-item v-if="authError" :wrapper-col="{ span: 24 }">
            <a-alert :message="authError" type="error" show-icon />
          </a-form-item>

          <a-form-item :wrapper-col="{ span: 24 }">
            <a-button
              :disabled="!emailFieldValue"
              :loading="loading"
              type="primary"
              html-type="submit"
              block
            >
              Send Reset Code
            </a-button>
          </a-form-item>

          <div style="text-align: center">
            <router-link to="/login">Back to Login</router-link>
          </div>
        </a-form>
      </template>

      <template v-else>
        <p style="text-align: center; margin-bottom: 24px">
          Enter the 6-digit verification code sent to <strong>{{ email }}</strong> and your new password.
        </p>

        <a-form @submit.prevent="resetPassword">
          <a-form-item
            label="Verification Code"
            :validate-status="resetErrors.otp ? 'error' : ''"
            :help="resetErrors.otp"
          >
            <a-input
              v-model:value="otp"
              placeholder="000000"
              :maxlength="6"
              style="text-align: center; font-size: 20px; letter-spacing: 8px; font-weight: 600"
            >
              <template #prefix>
                <SafetyOutlined />
              </template>
            </a-input>
          </a-form-item>

          <a-form-item
            label="New Password"
            :validate-status="resetErrors.password ? 'error' : ''"
            :help="resetErrors.password"
          >
            <a-input-password v-model:value="newPassword" autocomplete="new-password">
              <template #prefix>
                <LockOutlined />
              </template>
            </a-input-password>
          </a-form-item>

          <a-form-item
            label="Confirm Password"
            :validate-status="resetErrors.confirmPassword ? 'error' : ''"
            :help="resetErrors.confirmPassword"
          >
            <a-input-password v-model:value="confirmPassword" autocomplete="new-password">
              <template #prefix>
                <LockOutlined />
              </template>
            </a-input-password>
          </a-form-item>

          <a-form-item v-if="authError" :wrapper-col="{ span: 24 }">
            <a-alert :message="authError" type="error" show-icon />
          </a-form-item>

          <a-form-item :wrapper-col="{ span: 24 }">
            <a-button
              :disabled="!otp || !newPassword || !confirmPassword"
              :loading="loading"
              type="primary"
              html-type="submit"
              block
            >
              Reset Password
            </a-button>
          </a-form-item>

          <div style="text-align: center">
            <a-button type="link" @click="step = 'email'">
              <arrow-left-outlined /> Back
            </a-button>
          </div>
        </a-form>
      </template>
    </a-card>
  </div>
</template>
<style scoped>
.forgot-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 85vh;
}

.forgot-form {
  max-width: 400px;
  width: 100%;
}
</style>
